// src/domains/core/dashboard/store/widgetRegistry.ts

import type { WidgetMetadata } from "../types/widget.types";

/**
 * Widget Registry
 *
 * Zentrale Registrierung aller verfügbaren Dashboard-Widgets
 */
class WidgetRegistry {
  private widgets = new Map<string, WidgetMetadata>();

  /**
   * Registriert ein neues Widget
   *
   * @param metadata - Widget metadata to register
   *
   */
  register(metadata: WidgetMetadata): void {
    this.widgets.set(metadata.id, metadata);
  }

  /**
   * Gibt alle registrierten Widgets zurück
   *
   * @returns {WidgetMetadata[]} Array of all registered widgets
   *
   */
  getAll(): WidgetMetadata[] {
    return [...this.widgets.values()];
  }

  /**
   * Gibt Widgets nach Kategorie gefiltert zurück
   *
   * @param category - Widget category to filter by
   *
   * @returns {WidgetMetadata[]} Array of widgets in the specified category
   *
   */
  getByCategory(category: WidgetMetadata["category"]): WidgetMetadata[] {
    return this.getAll().filter((w) => w.category === category);
  }

  /**
   * Gibt ein Widget nach ID zurück
   *
   * @param id - Widget identifier
   *
   * @returns {WidgetMetadata | undefined} Widget metadata or undefined if not found
   *
   */
  get(id: string): undefined | WidgetMetadata {
    return this.widgets.get(id);
  }

  /**
   * Prüft ob ein Widget registriert ist
   *
   * @param id - Widget identifier
   *
   * @returns {boolean} True if widget is registered, false otherwise
   *
   */
  has(id: string): boolean {
    return this.widgets.has(id);
  }
}

/**
 * Singleton Widget Registry Instance
 */
export const widgetRegistry = new WidgetRegistry();

/**
 * Widget IDs - Zentrale Definition aller Widget-IDs
 */
export const WIDGET_IDS = {
  CODE_QUALITY: "code-quality",
  CODE_QUALITY_MATRIX: "code-quality-matrix",
  // Insights
  CONTEXT_INSIGHTS: "context-insights",
  GIT_STATUS: "git-status",
  // LLM
  LLM_SERVICES: "llm-services",
  MIRACLEFLOW_STATUS: "miracleflow-status",

  OURA_CORRECTION_FACTOR: "oura-correction-factor",
  OURA_DETAILED: "oura-detailed",
  // Health
  OURA_KEY_METRICS: "oura-key-metrics",
  OURA_STEPS: "oura-steps",
  // Investments
  PARQET_SUMMARY: "parqet-summary",

  REMINDERS: "reminders",
  // Admin
  ROADMAP_METRICS: "roadmap-metrics",
  ROADMAP_TOP_TASKS: "roadmap-top-tasks",
  // Smart Home
  SMARTHOME_STATUS: "smarthome-status",
  TIMETRACQ_DAILY_PLAN: "timetracq-daily-plan",

  // Productivity
  TIMETRACQ_TASKS: "timetracq-tasks",

  TIMETRACQ_TIME_TRACKING: "timetracq-time-tracking",

  WITHINGS_SUMMARY: "withings-summary",

  YAZIO_NUTRITION: "yazio-nutrition",
} as const;

/**
 * Initialisiert die Widget Registry mit Standard-Widgets
 */
export function initializeWidgetRegistry(): void {
  // Health Widgets
  widgetRegistry.register({
    category: "health",
    defaultSize: "medium",
    defaultVisible: true,
    description: "Activity, Readiness, and Sleep Scores",
    id: WIDGET_IDS.OURA_KEY_METRICS,
    name: "Oura Key Metrics",
  });

  widgetRegistry.register({
    category: "health",
    defaultSize: "medium",
    defaultVisible: true,
    description: "Daily steps with streak tracking",
    id: WIDGET_IDS.OURA_STEPS,
    name: "Oura Steps",
  });

  widgetRegistry.register({
    category: "health",
    defaultSize: "medium",
    defaultVisible: false,
    description: "Body composition and weight trends",
    id: WIDGET_IDS.WITHINGS_SUMMARY,
    name: "Withings Summary",
  });

  widgetRegistry.register({
    category: "health",
    defaultSize: "small",
    defaultVisible: false,
    description: "Daily nutrition summary",
    id: WIDGET_IDS.YAZIO_NUTRITION,
    name: "Yazio Nutrition",
  });

  widgetRegistry.register({
    category: "health",
    defaultSize: "medium",
    defaultVisible: false,
    description: "TDEE correction factor based on weight and intake",
    id: WIDGET_IDS.OURA_CORRECTION_FACTOR,
    name: "Oura Correction Factor",
  });

  // Productivity Widgets
  widgetRegistry.register({
    category: "productivity",
    defaultSize: "medium",
    defaultVisible: true,
    description: "Task overview and status",
    id: WIDGET_IDS.TIMETRACQ_TASKS,
    name: "TimeTracQ Tasks",
  });

  widgetRegistry.register({
    category: "productivity",
    defaultSize: "small",
    defaultVisible: false,
    description: "Today's plan summary",
    id: WIDGET_IDS.TIMETRACQ_DAILY_PLAN,
    name: "Daily Plan",
  });

  widgetRegistry.register({
    category: "productivity",
    defaultSize: "small",
    defaultVisible: true,
    description: "Session status and streak",
    id: WIDGET_IDS.MIRACLEFLOW_STATUS,
    name: "MiracleFlow Status",
  });

  widgetRegistry.register({
    category: "productivity",
    defaultSize: "small",
    defaultVisible: false,
    description: "Upcoming and overdue reminders",
    id: WIDGET_IDS.REMINDERS,
    name: "Reminders",
  });

  // Admin Widgets
  widgetRegistry.register({
    category: "admin",
    defaultSize: "medium",
    defaultVisible: false,
    description: "Key roadmap statistics",
    id: WIDGET_IDS.ROADMAP_METRICS,
    name: "Roadmap Metrics",
  });

  widgetRegistry.register({
    category: "admin",
    defaultSize: "small",
    defaultVisible: false,
    description: "Code quality analysis summary",
    id: WIDGET_IDS.CODE_QUALITY,
    name: "Code Quality",
  });

  widgetRegistry.register({
    category: "admin",
    defaultSize: "xlarge",
    defaultVisible: true,
    description: "Top 3 schlechteste Dateien nach Metrik",
    id: WIDGET_IDS.CODE_QUALITY_MATRIX,
    name: "Code Quality Matrix",
  });

  widgetRegistry.register({
    category: "admin",
    defaultSize: "medium",
    defaultVisible: true,
    description: "Top 3 Tasks nach Score",
    id: WIDGET_IDS.ROADMAP_TOP_TASKS,
    name: "Roadmap Top Tasks",
  });

  widgetRegistry.register({
    category: "admin",
    defaultSize: "small",
    defaultVisible: false,
    description: "Repository status overview",
    id: WIDGET_IDS.GIT_STATUS,
    name: "Git Status",
  });

  // Insights Widgets
  widgetRegistry.register({
    category: "insights",
    defaultSize: "large",
    defaultVisible: true,
    description: "Intelligent interpretations of your data",
    id: WIDGET_IDS.CONTEXT_INSIGHTS,
    name: "Contextual Insights",
  });

  // Investments Widgets
  widgetRegistry.register({
    category: "investments",
    defaultSize: "medium",
    defaultVisible: false,
    description: "Portfolio summary and recent transactions",
    id: WIDGET_IDS.PARQET_SUMMARY,
    name: "Parqet Portfolio",
  });

  // LLM Widgets
  widgetRegistry.register({
    category: "llm",
    defaultSize: "small",
    defaultVisible: false,
    description: "Quick access to ChatGPT and Ollama",
    id: WIDGET_IDS.LLM_SERVICES,
    name: "LLM Services",
  });

  // Smart Home Widgets
  widgetRegistry.register({
    category: "smarthome",
    defaultSize: "small",
    defaultVisible: false,
    description: "Device status and quick controls",
    id: WIDGET_IDS.SMARTHOME_STATUS,
    name: "Smart Home",
  });
}
